import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import Button from '@/components/ui/Button';
import w from './SafetyEnv.module.scss';
import s from './ProductWhite.module.scss'; // [2026-10-26] ① 제조범위 AGENDA 전폭 밴드 재사용(이미지46). 밴드 전용 클래스만 s에서.
import { GUIDE_ICON } from '@/components/GuideIcons';

// ============================================================================
//  환경 — IoT 게이트웨이 제조  (/business/igm)
//  [2026-10-08] 기획서 v7 4-2. 6영역: ①제조범위 ②제품계열 ③개발구조 ④품질관리 ⑤인증·연구소 ⑥협력문의.
//  [2026-10-22 사용자] ★본문 화이트화(SafetyEnv.module) + 다이어그램 '페이지당 1개'.
//    → 유일 다이어그램 = ④ 품질관리(입고→출하 5단계 프로세스) 셰브론. 나머지=단순 비비드 카드/칩.
//    히어로=다크 유지. 배경 위 글자=어두운색, 컬러 채운 요소 위 글자=흰색.
//  실자료(제조·시험 사진·품질검사 절차·인증 증빙) 미보유 → Soon 준비중.
//  '기술 페이지'→/company/rd, '인증 페이지'→/company/certifications 매핑.
// ============================================================================

const HERO_IMG = '/images/hero/hero-guide.jpg';
const SLOGAN = '하드웨어·펌웨어·응용 소프트웨어를 자체 개발하고, 현장에서 쌓은 경험을 다시 제품 개선으로 연결하는 EVS의 IoT 게이트웨이 제조 역량입니다.';

const Ic = ({ n }: { n: string }) => <div className={w.iconWrap}>{GUIDE_ICON[n]}</div>;
const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};
function Soon({ label }: { label: string }) {
  return (
    <div className={w.soon} role="img" aria-label={`${label} 준비중`}>
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <span>{label} · 준비중</span>
    </div>
  );
}

export default function Igm() {
  const navigate = useNavigate();
  const nav = (to: string) => navigate(to);
  return (
    <main className={w.page}>
      <PageLoader />
      <header className={w.hero}>
        <div className={w.heroBg} style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className={w.heroOverlay} />
        <HeroCrumbs items={['HOME', '환경', 'IoT 게이트웨이 제조']} />
        <div className={w.heroInner}>
          <h1 className={w.heroTitle}>IoT 게이트웨이 제조</h1>
          <p className={w.heroSub}>{SLOGAN}</p>
          <div className={w.ctaRow} style={{ marginTop: 28 }}>
            <Button variant="pill" onClick={() => nav('/support/inquiry?from=/business/igm')}>제품·협력 문의</Button>
            <a className={w.ctaGhost} onClick={() => nav('/products/evs-g200')}>EVS-G200 제품 보기</a>
          </div>
        </div>
      </header>

      {/* ① 제조 범위 — [2026-10-26 사용자] AGENDA 전폭 그라데이션 밴드(이미지46 스타일)로 재디자인.
          큰 번호 지그재그 + 주제별 아이콘(응용SW는 GUIDE_ICON 'monitor'=globe 버그 회피해 'display' 사용).
          밴드 클래스는 s(ProductWhite)에서 재사용. 섹션 배경은 s.agenda가 전폭 그라데이션으로 덮음. */}
      <section className={`${s.section} ${s.agenda}`}>
        <Reveal>
          <p className={s.agendaEyebrow}>MANUFACTURING SCOPE</p>
          <h2 className={s.agendaHead}>제조 범위</h2>
          <p className={s.agendaSub}>하드웨어 설계부터 펌웨어·응용 소프트웨어·연동·버전관리까지 자체 역량으로 수행합니다.</p>
          <div className={s.agendaGrid5}>
            {[
              { n: '01', i: 'gateway', t: '하드웨어', d: '게이트웨이 함체·보드·입출력 구성 설계.' },
              { n: '02', i: 'solution', t: '펌웨어', d: '계측·수집·전송 펌웨어 개발과 유지.' },
              { n: '03', i: 'display', t: '응용 소프트웨어', d: '현장 표시·관제 연동 응용 SW.' },
              { n: '04', i: 'signal', t: '연동', d: '계측기·상위 관제·GreenLink 연동.' },
              { n: '05', i: 'database', t: '버전관리', d: '모델·펌웨어 버전 이력 관리.' },
            ].map((x, i) => (
              <div key={x.n} className={`${s.agendaCol} ${i % 2 === 1 ? s.agendaLow : ''}`}>
                <div className={s.agendaIco}>{GUIDE_ICON[x.i]}</div>
                <span className={s.agendaNum}>{x.n}</span>
                <span className={s.agendaLine} />
                <div>
                  <div className={s.agendaTitle}>{x.t}</div>
                  <div className={s.agendaDesc}>{x.d}</div>
                </div>
              </div>
            ))}
          </div>
          <a className={s.agendaMore} onClick={() => nav('/company/rd')}>연구개발·기술 보기 →</a>
        </Reveal>
      </section>

      {/* ② 제품 계열 — 비비드 카드 */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>PRODUCT LINEUP</p>
            <h2 className={w.h2}>제품 계열</h2>
            <p className={w.lead}>현장 규모·배출구 수에 맞춰 단수형·복수형·교대형으로 제공하며, 입력 채널을 확장 구성할 수 있습니다.</p>
            <div className={`${w.grid} ${w.grid3}`}>
              <div className={`${w.card} ${w.cBlue} ${w.solid}`}><Ic n="gateway" /><h3>단수형</h3><p>단일 배출구·소규모 현장용 기본 구성.</p></div>
              <div className={`${w.card} ${w.cTeal} ${w.solid}`}><Ic n="cycle" /><h3>복수형</h3><p>다수 배출구·계측 채널 확장 구성.</p></div>
              <div className={`${w.card} ${w.cIndigo} ${w.solid}`}><Ic n="toggle" /><h3>교대형</h3><p>교대 가동 설비에 맞춘 채널 운용.</p></div>
            </div>
            <a className={w.moreLink} onClick={() => nav('/products/evs-g200')}>EVS-G200 상세 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ③ 개발 구조 — 기술 칩 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>DEVELOPMENT</p>
            <h2 className={w.h2}>개발 구조</h2>
            <p className={w.lead}>검증된 기술 스택으로 안정적으로 동작하도록 설계했습니다. (공개 가능 범위)</p>
            {/* [2026-10-22 사용자] 기술 칩이 '밋밋' → 참고 이미지(컬러바+번호+아이콘) 스텝 리스트로. 레인보우 색·관련 아이콘. */}
            <div className={w.stepList}>
              {[
                { n: '01', t: 'Windows 10 IoT', i: 'gateway', d: '산업용 IoT 전용 OS 기반으로 안정 동작.', fc: '#e0983a' },
                { n: '02', t: 'C#', i: 'code', d: '.NET 기반 수집·표시·전송 응용 로직.', fc: '#e0564d' },
                { n: '03', t: 'Serial · Socket', i: 'signal', d: '계측기·장비와의 현장 통신 처리.', fc: '#2ca16a' },
                { n: '04', t: 'SQLite', i: 'database', d: '로컬 데이터 저장·장애 시 보존.', fc: '#1aafc0' },
                { n: '05', t: 'TCP · JSON', i: 'cycle', d: '상위 관제·GreenLink 전송 포맷.', fc: '#c159a8' },
              ].map((x) => (
                <div key={x.n} className={w.stepItem} style={{ ['--fc' as string]: x.fc } as React.CSSProperties}>
                  <span className={w.stepNum}>{x.n}</span>
                  <div className={w.stepIco}>{GUIDE_ICON[x.i]}</div>
                  <div className={w.stepTx}><strong>{x.t}</strong><span>{x.d}</span></div>
                </div>
              ))}
            </div>
            <a className={w.moreLink} onClick={() => nav('/company/rd')}>기술 소개 보기 →</a>
          </Reveal>
        </div>
      </section>

      {/* ④ 품질관리 — ★유일 다이어그램: 셰브론(입고→출하 5단계) */}
      <section className={`${w.section} ${w.alt}`}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>QUALITY</p>
            <h2 className={w.h2}>품질관리</h2>
            <p className={w.lead}>입고부터 출하까지 단계별 검사로 품질을 확보합니다.</p>
            {/* [2026-10-22 사용자] ★'페이지당 다이어그램 1개' 재확정(남발 금지).
                igm의 유일 다이어그램 = ③ 개발구조 stepList(사용자 승인). 따라서 여기 ④ 품질관리는
                셰브론(2번째 다이어그램)에서 '단순 비비드 카드'로 격하. 입고→출하 순서는 번호(01~05)로 표현.
                변경금지 트리거: 이 섹션을 다시 셰브론/스텝 등 다이어그램으로 되돌리면 igm 다이어그램이 2개가 됨. */}
            <div className={`${w.grid} ${w.grid5}`}>
              {[
                { n: '01', t: '입고검사', i: 'action', d: '부품·자재 입고 확인', c: w.cBlue },
                { n: '02', t: '조립', i: 'wrench', d: '보드·함체 조립', c: w.cTeal },
                { n: '03', t: '통신검사', i: 'signal', d: '통신·전송 점검', c: w.cIndigo },
                { n: '04', t: '연동검사', i: 'gateway', d: '계측·관제 연동 확인', c: w.cAmber },
                { n: '05', t: '출하검사', i: 'certified', d: '최종 동작·출하 검사', c: w.cCoral },
              ].map((x) => (
                <div key={x.n} className={`${w.card} ${x.c} ${w.solid}`}>
                  <Ic n={x.i} /><h3>{x.n} {x.t}</h3><p>{x.d}</p>
                </div>
              ))}
            </div>
            <Soon label="품질검사 절차·제조·시험 사진" />
          </Reveal>
        </div>
      </section>

      {/* ⑤ 인증·연구소 — 비비드 카드 */}
      <section className={w.section}>
        <div className={w.inner}>
          <Reveal>
            <p className={w.eyebrow}>CERTIFICATION · R&amp;D</p>
            <h2 className={w.h2}>인증 · 연구소</h2>
            <p className={w.lead}>KC 인증과 한국환경공단 관련 문서를 보유하며, 기업부설연구소에서 제품을 개발·개선합니다.</p>
            <div className={`${w.grid} ${w.grid3}`}>
              <div className={`${w.card} ${w.cTeal} ${w.solid}`}><Ic n="certified" /><h3>KC 인증</h3><p>게이트웨이 전 모델 KC 인증.</p></div>
              <div className={`${w.card} ${w.cBlue} ${w.solid}`}><Ic n="doc" /><h3>환경공단 문서</h3><p>대기배출 IoT 관련 승인·확인 문서.</p></div>
              <div className={`${w.card} ${w.cIndigo} ${w.solid}`}><Ic n="solution" /><h3>기업부설연구소</h3><p>HW·펌웨어·응용 SW 자체 개발.</p></div>
            </div>
            <div className={w.ctaRow} style={{ justifyContent: 'flex-start', marginTop: 28 }}>
              <a className={w.ctaGhost} onClick={() => nav('/company/certifications')}>인증·기술 페이지</a>
            </div>
            <Soon label="인증·연구소 증빙(증서·번호·발급일)" />
          </Reveal>
        </div>
      </section>

      {/* ⑥ 협력 문의 — CTA 틴트밴드 */}
      <section className={w.finalCta}>
        <div className={w.inner}>
          <Reveal>
            <h2 className={w.h2}>제품 · 연동 · 협력을 문의하세요</h2>
            <p className={w.lead}>OEM·공동개발·연동 등 제조 협력 문의를 환영합니다.</p>
            <div className={w.ctaRow}>
              <Button variant="pill" onClick={() => nav('/support/inquiry?from=/business/igm')}>협력 문의</Button>
              <a className={w.ctaGhost} onClick={() => nav('/support/consult')}>제품 상담</a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
